import { useStore } from "@nanostores/react";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { dataStore, filteredValuesStore, setTypeValues, updateUrl } from "../../_utils/searchStore";
import type { Value } from "../../_utils/types";
import { Checkbox } from "./Checkbox";
import { Collapse } from "./Collapse";
import { MultiSelect } from "./MultiSelect";
import { drawerTypeStore } from "./store";

const ALL_BUDGETS = [
  { label: "Moins de 2 000 €", value: "lte2000" },
  { label: "Entre 2 000 et 3 000 €", value: "lte3000" },
  { label: "Entre 3 000 et 5 000€", value: "lte5000" },
  { label: "Plus de 5 000 €", value: "gt5000" },
];

type Props = {
  isDesktop?: boolean;
  fit?: boolean;
};
export const Budget = ({ isDesktop = false, fit = false }: Props) => {
  const router = useRouter();
  const state = useStore(drawerTypeStore);
  const selectedBudgets = useStore(filteredValuesStore("budgets"));
  const [localBudgets, setLocalBudgets] = useState(selectedBudgets);
  const selectedBudgetsRef = useRef(selectedBudgets);
  const data = useStore(dataStore);

  useEffect(() => {
    const isDiff = () => {
      const oldT = selectedBudgetsRef.current.sort((a, b) => a.value.localeCompare(b.value));

      const newT = selectedBudgets.sort((a, b) => a.value.localeCompare(b.value));

      if (oldT.length !== newT.length) return true;

      for (let index = 0; index < newT.length; index++) {
        if (oldT[index] !== newT[index]) return true;
      }

      return false;
    };

    if (isDiff()) {
      selectedBudgetsRef.current = selectedBudgets;
      setLocalBudgets(selectedBudgets);
    }
  }, [selectedBudgets]);

  const handleOpen = () => {
    drawerTypeStore.set(state === "budget" ? null : "budget");
  };

  const handleChange = (newValue: Value, directUpdate = false) => {
    let newBudgets: Array<Value> = [];
    if (localBudgets.some((lt) => lt.value === newValue.value)) {
      newBudgets = localBudgets.filter((lt) => lt.value !== newValue.value);
    } else {
      newBudgets = [...localBudgets, newValue];
    }

    setLocalBudgets(newBudgets);

    if (directUpdate) {
      setTypeValues("budgets", newBudgets);
      updateUrl(router);
    }
  };

  const handleClickOutside = () => {
    setTypeValues("budgets", localBudgets);

    drawerTypeStore.set(null);
  };

  const handleValidate = () => {
    setTypeValues("budgets", localBudgets);
    updateUrl(router);
  };

  const handleClear = () => {
    setTypeValues("budgets", []);
    updateUrl(router);
  };

  const budgets = [
    ...selectedBudgets
      .filter((s) => !data?.budgets.some((t) => t.key === s.value))
      .map((s) => ({
        value: s.value,
      })),
    ...(data?.budgets.map((t) => ({ value: t.key })) || []),
  ];

  return isDesktop ? (
    <MultiSelect
      fit={fit}
      placeholder="Budget"
      type="budgets"
      onChange={handleChange}
      onValidate={handleValidate}
      onClear={handleClear}
      localValues={localBudgets}
      selectedValues={selectedBudgets}
      values={ALL_BUDGETS.filter((b) => budgets.some((budget) => budget.value === b.value))}
      disableSort
      disableSearch
    />
  ) : (
    <Collapse
      open={state === "budget"}
      title="Budget"
      onclick={handleOpen}
      onClickOutside={handleClickOutside}
      count={selectedBudgets.length}
    >
      <div className="mt-5 grid sm:grid-cols-2 gap-[10px]">
        {ALL_BUDGETS.filter((b) => budgets.some((budget) => budget.value === b.value)).map((budget) => (
          <Checkbox
            key={budget.value}
            label={budget.label}
            checked={localBudgets.some((lt) => lt.value === budget.value)}
            onChange={() =>
              handleChange({
                type: "budgets",
                value: budget.value,
              })
            }
          />
        ))}
      </div>
    </Collapse>
  );
};
